import { normalizeExerciseName } from '@gymcloud/mixedscript'

import type { LoggedSet, TimelineGroup } from '~/data/types'
import { dayKey } from '~/lib/dates'

/**
 * How a pile of sets divides up: by the workout each belongs to, and nothing finer. A workout's
 * sets are one block of tiles — no exercise heads any of them, because every tile names its own
 * exercise, and a workout that goes squat, bench, squat, bench was being carved into four
 * headings over four tiles, each saying a name the tile under it already said.
 *
 * Besides its size a group counts its warm-ups, which is what tells the timeline how many tiles
 * it will draw before any of its sets have loaded.
 */
export function groupsOf(
  ascending: readonly LoggedSet[],
  nameOf: (workoutId: string) => string | null,
): TimelineGroup[] {
  const groups: TimelineGroup[] = []
  ascending.forEach((set, at) => {
    const previous = ascending[at - 1]
    const group = groups[groups.length - 1]
    if (group === undefined || previous?.workoutId !== set.workoutId) {
      groups.push({
        workoutId: set.workoutId,
        workoutName: set.workoutId === null ? null : nameOf(set.workoutId),
        count: 1,
        warmups: set.isWarmup ? 1 : 0,
      })
      return
    }
    groups[groups.length - 1] = {
      ...group,
      count: group.count + 1,
      warmups: group.warmups + (set.isWarmup ? 1 : 0),
    }
  })
  return groups
}

/**
 * The number each set carries, aligned with `ascending`: which set of its own exercise it was,
 * counting from the first one performed, with warm-ups counted apart from working sets.
 *
 * The count is the exercise's and runs the length of the run — a squat done after the pull-ups
 * is still the third squat, which is the number the tracker gave it while it was being done.
 * Numbered over the whole run before anything is filtered out, so hiding the warm-ups never
 * renumbers the sets that stay.
 */
export function numbersInRun(ascending: readonly LoggedSet[]): number[] {
  const counts = new Map<string, { working: number; warmup: number }>()
  return ascending.map((set) => {
    const key = normalizeExerciseName(set.exerciseName)
    const held = counts.get(key) ?? { working: 0, warmup: 0 }
    const next = set.isWarmup
      ? { ...held, warmup: held.warmup + 1 }
      : { ...held, working: held.working + 1 }
    counts.set(key, next)
    return set.isWarmup ? next.warmup : next.working
  })
}

/** The number one set carries in a run read oldest first, or null when the run does not hold it. */
export function numberOfSetInRun(ascending: readonly LoggedSet[], setId: string): number | null {
  const at = ascending.findIndex((set) => set.id === setId)
  return at === -1 ? null : (numbersInRun(ascending)[at] ?? null)
}

/** One exercise of a workout, gathered: every set of it with the number each carries. */
export interface ExerciseBlock {
  /** The exercise as its first set named it. */
  readonly exerciseName: string
  /** Every set of it, warm-ups included, in the order they were performed. */
  readonly sets: readonly LoggedSet[]
  /** Aligned with `sets`. */
  readonly numbers: readonly number[]
}

/**
 * A workout gathered by exercise: a block for each exercise it holds, holding every set of it
 * wherever in the workout that set was performed. The blocks stand in the order their exercises
 * were first performed, and each block's sets in the order they were.
 *
 * A superset is no exception. Its sets go to their own exercises' blocks, because a block headed
 * by one exercise is a claim about all of that exercise, and that is the claim worth making.
 */
export function exerciseBlocksOf(ascending: readonly LoggedSet[]): ExerciseBlock[] {
  const numbers = numbersInRun(ascending)
  const blocks = new Map<string, { exerciseName: string; sets: LoggedSet[]; numbers: number[] }>()
  ascending.forEach((set, at) => {
    const key = normalizeExerciseName(set.exerciseName)
    const block = blocks.get(key) ?? { exerciseName: set.exerciseName, sets: [], numbers: [] }
    block.sets.push(set)
    block.numbers.push(numbers[at] ?? 0)
    blocks.set(key, block)
  })
  return [...blocks.values()]
}

/**
 * The timeline's own order: days newest first, and inside a day the sets in the order they were
 * performed, which is how they are dealt into their blocks. `state.sets` runs newest first the
 * whole way down, which is not what the page looks like — a deck in that order puts the first
 * tile of the newest day several places in, so leafing back from the very first thing on the
 * page still goes somewhere and leafing on from it skips the rest of that day.
 */
export function timelineOrder(sets: readonly LoggedSet[]): LoggedSet[] {
  const byDay = new Map<string, LoggedSet[]>()
  for (const set of sets) {
    const day = dayKey(set.performedAt)
    const held = byDay.get(day)
    if (held) {
      held.push(set)
    } else {
      byDay.set(day, [set])
    }
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .flatMap(([, ofDay]) => [...ofDay].reverse())
}
